import { AuthScreen } from "../auth/AuthScreen";
import { SystemState } from "../../shared/components/SystemState";
import { useShiftProductionFormWorkspace } from "../shift-production-form/useShiftProductionFormWorkspace";
import { ProductionAnalysisPage } from "./ProductionAnalysisPage";
import { ProductionAnalysisPreview } from "./ProductionAnalysisPreview";
import { useAnalysisDailyCoverage } from "./useAnalysisDailyCoverage";
import { useProductionAnalysisWorkspace } from "./useProductionAnalysisWorkspace";
import { clearAnalysisFactoryReportCache } from "./analysis-factory-reports";
import { analysisAccessState } from "./analysis-access-state";
import { PinAccessForm } from "../../shared/components/PinAccessForm";
import { useEffect, useState } from "react";
import "../shift-production-form/shift-production-form.css";
import "./production-analysis.css";

function AnalysisWorkspace({ name, actorScope, testMode }: { name: string; actorScope: string; testMode: boolean }) {
  const workspace = useProductionAnalysisWorkspace(actorScope, testMode);
  const coverage = useAnalysisDailyCoverage(workspace.filters.dateFrom, workspace.filters.dateTo, workspace.location.departmentName, testMode);
  const [sourceRevision, setSourceRevision] = useState<number | undefined>();
  const [sourceKind, setSourceKind] = useState<"approved" | "pending">("approved");
  useEffect(() => () => clearAnalysisFactoryReportCache(actorScope), [actorScope]);
  return <>
    <ProductionAnalysisPage
      testMode={testMode}
      name={name}
      dailyCoverage={coverage}
      {...workspace}
      onReload={() => void workspace.reload()}
      onOpen={(id, revisionNumber, kind = "approved") => { setSourceRevision(revisionNumber); setSourceKind(kind); void workspace.openForm(id); }}
      onDepartment={workspace.selectDepartment}
      onMachine={workspace.selectMachine}
      onMachineShortcut={workspace.selectMachineShortcut}
      onCell={workspace.selectShift}
      onGlobalShift={workspace.selectGlobalShift}
      onBreadcrumb={workspace.goToLevel}
    />
    {workspace.selected ? <ProductionAnalysisPreview kind={sourceKind} form={workspace.selected} revisionNumber={sourceRevision ?? workspace.dashboard?.rows.find(row => row.id === workspace.selected?.id)?.revisionNumber} onClose={workspace.closeForm} /> : null}
  </>;
}
export function ProductionAnalysisModule({ testMode = false }: { testMode?: boolean }) {
  const sessionWorkspace = useShiftProductionFormWorkspace();
  const { session, reload } = sessionWorkspace;
  const accessState = analysisAccessState(session.phase, session.phase === "ready" ? session.actor : null);
  if (accessState === "LOADING") return <div className="pa-session-shell" data-theme="light"><SystemState mode="page" tone="loading" title="Analiz hazırlanıyor" description="Oturumunuz doğrulanıyor." /></div>;
  if (accessState === "SERVICE_UNAVAILABLE") return <div className="pa-session-shell" data-theme="light"><AuthScreen mode="service-unavailable" onRetry={reload} /></div>;
  if (accessState === "PIN") return <PinAccessForm
    verify={(pin) => sessionWorkspace.verifyApproverPin(pin).then(sessionWorkspace.acceptSession)}
    eyebrow="Üretim yönetimi"
    title={session.phase === "ready" && session.actor ? "Analiz yetkisi bulunamadı" : "Üretim analizine giriş"}
    description={session.phase === "ready" && session.actor ? "Bu oturum üretim analizini görüntüleyemez. Üretim yöneticisi veya sistem yöneticisi PIN’iyle devam edin." : "Size tanımlanan 6 haneli kişisel yönetici PIN’inizi girin."}
    label="Yönetici PIN’i"
    submitLabel="Analizi aç"
    scope="Yalnız yetkili olduğunuz hatların onaylı üretim verileri gösterilir."
  />;
  if (session.phase !== "ready" || !session.actor) return null;
  const actorScope = `${session.actor.kind.toLocaleLowerCase("tr-TR")}:${session.actor.id}:${session.expiresAt ?? "session"}:${testMode ? "test" : "real"}`;
  return <AnalysisWorkspace key={actorScope} name={session.actor.name} actorScope={actorScope} testMode={testMode} />;
}
